import { type ComponentType, type ReactNode, useState } from 'react';

import { Stack, Typography, type TypographyProps } from '@mui/material';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import styles from './ExpandableCard.module.scss';
import { CardGridItem } from '@/components/layouts/CardGridItem';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';

export interface RenderBottomPayload {
  cardId: string;
  cardHovered: boolean;
}
export interface ExpandableCardProps {
  id: string;
  onSelect: (id: string) => void;
  title?: string;
  description?: string;
  renderBottom?: (payload: RenderBottomPayload) => ReactNode;
}

const MotionStack = motion.create(Stack);
const MotionTypography = motion.create(
  Typography as ComponentType<TypographyProps>, // otherwise TS complains about non-existing component prop
);

export const ExpandableCard = ({
  id,
  onSelect,
  title,
  description,
  renderBottom,
}: ExpandableCardProps) => {
  const { t } = useTranslation('ui');
  const [cardHovered, setCardHovered] = useState(false);

  return (
    <motion.div
      layoutId={id}
      data-automation-id={`expandable-card-${id}`}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('button, a')) return;
        onSelect(id);
      }}
      className={styles.MotionWrapper}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(id);
        }
      }}
      onMouseEnter={() => setCardHovered(true)}
      onMouseLeave={() => setCardHovered(false)}
    >
      <CardGridItem className={styles.ExpandableCard} autoTopMarginOn="bottom">
        <CardGridItem.Top
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {title && (
            <MotionTypography
              layoutId={`card-title-${id}`}
              variant="bodyBold"
              color="text.primary"
              component="h3"
            >
              {title}
            </MotionTypography>
          )}
          <motion.div
            whileHover={{ scale: 1.15 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            style={{ willChange: 'transform' }}
          >
            <IconButton
              component="button"
              data-automation-id={`expandable-card-expand-button-${id}`}
              selected={cardHovered}
              variant="rounded"
              iconName={uiIcons.controls.expand}
              tooltip={t('expandableCards.ExpandButtonDefaultTooltip')}
              size="small"
              color="info"
              onClick={() => onSelect(id)}
            />
          </motion.div>
        </CardGridItem.Top>
        {description && (
          <CardGridItem.Middle>
            <MotionTypography
              className={styles.CardDescription}
              layoutId={`card-description-${id}`}
              variant="bodyRegular"
              color="text.secondary"
            >
              {description}
            </MotionTypography>
          </CardGridItem.Middle>
        )}
        {renderBottom && (
          <CardGridItem.Bottom>
            <MotionStack
              layoutId={`card-bottom-${id}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              {/* have cardHovered state exposed for styling of bottom content */}
              {renderBottom({ cardId: id, cardHovered })}
            </MotionStack>
          </CardGridItem.Bottom>
        )}
      </CardGridItem>
    </motion.div>
  );
};
